import { MoreVertRounded } from '@mui/icons-material';
import { Grid, Typography } from '@mui/material';
import { isEmpty } from 'lodash';
import { type MouseEvent, useEffect, useState } from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import { Snackbar } from 'src/components/snackbar';
import {
  InputWidget,
  NumberInputWidget,
  SelectDropdownWidget,
} from 'src/components/widget';
import { PATHS } from 'src/constants/paths';
import { ProductVariationMenu } from 'src/features/products/addProductPage/modules/productVariationMenu/ProductVariationMenu';
import {
  useDeleteDyDisplay,
  useDeleteProductVariation,
  useGetProductVariationFlavors,
  useGetProductVariationTypes,
  useMenu,
  useModal,
} from 'src/hooks';
import { validateProductCode } from 'src/hooks/api/product/helper';
import { useScrollToHash } from 'src/hooks/useScrollToHash';
import { IProduct } from 'src/interfaces';
import { lang, useUserRole } from 'src/utils';

import { SimpleDialog } from '../modal';
import { ProductVariationAssets } from '../productVariationTable';
import { IProductVariationAsset } from '../productVariationTable/ProductVariationAssets.interface';
import { ProductVariationDyDisplay } from '../productVariationTable/ProductVariationDyDisplay';
import { CreateVariationFlavorModal } from './models/CreateVariationFlavorModal';
import { CreateVariationSizeModel } from './models/CreateVariationSizeModel';
import { ProductVariationFormProps } from './ProductVariationForm.interface';
import {
  ProductVariationHeader,
  ProductVariationIcon,
  StyledGrid,
} from './ProductVariationForm.styled';

const statusOptions = [
  { value: true, label: 'Active' },
  { value: false, label: 'Inactive' },
];

export const ProductVariationForm = (props: ProductVariationFormProps) => {
  const { data: productVariationTypes = [] } = useGetProductVariationTypes([
    'sort=*name',
  ]);
  const { data: productVariationFlavors = [] } = useGetProductVariationFlavors([
    'sort=*name',
  ]);
  useScrollToHash();

  const createVariationSizeModel = useModal();

  const [deletingStatus, setDeletingStatus] = useState(false);

  const { control, formState, watch, setValue, getValues } = useFormContext<
    IProduct & { asset?: File }
  >();

  const variationAssets =
    watch(`productVariations.${props.index}.variationAssets`) || [];

  const getIndex = (index: number) => {
    // if groupProductImage exists, it is index 0 in the UI array
    return props.groupProductImage ? index - 1 : index;
  };

  const getThemesId = (themeNames: string[]): number[] => {
    return (
      props?.themes
        ?.filter((theme) => themeNames.includes(theme.name))
        .map((theme) => theme.id) || []
    );
  };
  const onChangeVariationAsset = (
    asset: any,
    index?: number,
    themes?: string[],
  ) => {
    const themeIds: number[] = getThemesId(themes || []);
    const imageUrl = URL.createObjectURL(asset);
    const assetData: IProductVariationAsset = {
      assetUrl: imageUrl,
      assetName: asset?.name,
      themeIds: themeIds ? themeIds : [],
      isDY: props.groupProductImage
        ? false
        : !variationAssets.some((asset) => !!asset.isDY),
    };

    if (index !== undefined) {
      variationAssets[getIndex(index)] = assetData;
    } else {
      variationAssets.push(assetData);
    }

    setValue(
      `productVariations.${props.index}.variationAssets`,
      variationAssets,
    );
  };

  const onChangeVariationTheme = (index: number, themeNames: string[]) => {
    const themeIds: number[] = getThemesId(themeNames);
    const variationAssets =
      getValues(`productVariations.${props.index}.variationAssets`) || [];
    variationAssets[getIndex(index)].themeIds = themeIds;
    setValue(
      `productVariations.${props.index}.variationAssets`,
      variationAssets,
    );
  };

  const onChangeVariationDY = (index: number) => {
    // reset isDY
    const variationAssets = (
      getValues(`productVariations.${props.index}.variationAssets`) || []
    ).map((asset) => ({
      ...asset,
      isDY: false,
    }));

    if (props.groupProductImage && index === 0) {
      // dy asset fallbacks to groupProductImage
      setValue(
        `productVariations.${props.index}.variationAssets`,
        variationAssets,
      );
      setValue(`productVariations.${props.index}.dy_asset`, null);
    } else {
      variationAssets[getIndex(index)].isDY = true;

      setValue(
        `productVariations.${props.index}.variationAssets`,
        variationAssets,
      );
    }
  };

  const deleteVariationAsset = (index: number) => {
    const variationAssets =
      getValues(`productVariations.${props.index}.variationAssets`) || [];

    variationAssets.splice(getIndex(index), 1);
    setValue(
      `productVariations.${props.index}.variationAssets`,
      variationAssets,
    );
    const dyAsset = variationAssets.find((a) => !!a.isDY)?.assetId ?? null;
    setValue(
      `productVariations.${props.index}.dy_asset` as 'productVariations.0.dy_asset',
      dyAsset,
    );
  };

  const groupProductCode = watch('product_code');
  const productVariations = watch('productVariations');

  const updatedProductVariations = productVariations.map((v) => {
    const productVariant = productVariationTypes.find(
      (e) => e.id === v.product_variation_size_id,
    );
    const productVariantFlavor = productVariationFlavors.find(
      (e) => e.id === v.product_variation_flavor_id,
    );

    return {
      ...v,
      productVariantName: productVariant?.name || 'None',
      productFlavorName: productVariantFlavor?.name || 'None',
    };
  });

  if (
    JSON.stringify(productVariations) !==
    JSON.stringify(updatedProductVariations)
  ) {
    setValue('productVariations', updatedProductVariations);
  }

  const productVariationMenu = useMenu();
  const deleteProductVariationModal = useModal();
  const createVariationFlavorModel = useModal();
  const deleteDyDisplay = useDeleteDyDisplay();
  const blockingDeletionModal = useModal();

  const deleteProductVariation = useDeleteProductVariation(false);
  const navigate = useNavigate();

  useEffect(() => {
    if (props.index === 0) {
      setValue(
        `productVariations.${props.index}.product_code`,
        groupProductCode,
      );
    }
  }, [props.index, setValue, groupProductCode]);

  const { isViewOnly } = useUserRole();

  const getProductVariationTypeOptions = () => {
    return productVariationTypes.map((item) => {
      const isVariationTypeFlavorAdded = productVariations.some(
        (product) =>
          Number(product?.product_variation_size_id) === item?.id &&
          productVariations?.[props.index]?.product_variation_flavor_id ===
            product?.product_variation_flavor_id,
      );

      return {
        label: item.name || 'None',
        value: item.id,
        disabled: isVariationTypeFlavorAdded,
      };
    });
  };

  const productVariationFlavorOptions = productVariationFlavors.map((item) => ({
    label: item.name || 'None',
    value: item?.id,
  }));

  const onValidate = async (value: string) => {
    if (!props.isProductCodeEnabled) return undefined;
    const exists = await validateProductCode(value);

    return exists ? 'Product code already exist' : undefined;
  };

  const onMenuClick = (event: MouseEvent<HTMLElement>) => {
    event.stopPropagation();
    productVariationMenu.onOpenMenu(event.currentTarget);
  };

  const handleDeleteProductVariation = async () => {
    const index = props.index;
    const groupProductCode = props.productVariation?.group_product_code;
    const productCode = props.productVariation?.product_code;
    const productVariationName = props.productVariation?.name;

    deleteProductVariationModal.onCloseModal();

    if (!productCode || !groupProductCode) {
      return;
    }

    const data = {
      productCode,
      groupProductCode,
      index,
      product_category_names: watch('product_category_names') || [],
      productVariationName: productVariationName || '',
    };

    try {
      setDeletingStatus(true);
      await deleteProductVariation.mutateAsync(data);

      Snackbar.success(
        `${props.productVariation?.name} ${lang.successfullyDeleted}`,
      );

      if (index === 0) {
        navigate(PATHS.PRODUCTS);
      }

      props?.resetFormValue?.();
    } catch (error: any) {
      setDeletingStatus(false);
      const msg = error.response?.data?.error || error.message;
      if (msg?.includes('DY display mapping:')) {
        blockingDeletionModal.onOpenModal();
      } else {
        Snackbar.error(msg);
      }
    }
  };

  const handleDeleteDyDisplay = async () => {
    const variation = props.productVariation;
    const templateId = variation?.variation_template?.id;
    const templateName = variation?.variation_template?.template_name;
    const variationName = variation?.name;

    if (!templateId || !templateName || !variationName) return;

    setValue(`productVariations.${props.index}.variation_template`, null);

    try {
      await deleteDyDisplay.mutateAsync({
        templateId,
        templateName,
        variationName,
      });
      Snackbar.success(`${lang.dyDisplay} ${lang.successfullyDeleted}`);
    } catch {
      setValue(
        `productVariations.${props.index}.variation_template`,
        variation.variation_template,
      );
      Snackbar.error(lang.failedDeleteText);
    }
  };

  const errors = formState.errors.productVariations?.[props.index];

  return (
    <Grid container alignItems="center" justifyContent="center">
      <Grid item xs={12}>
        <ProductVariationHeader>
          <Typography
            variant="h4"
            fontWeight="400"
            ml={2}
            sx={{ flexGrow: 1 }}
            // anchor scrolling id
            id={props.productVariation?.product_code}>
            {lang.variationDetails}
          </Typography>
          {!props.isProductCodeEnabled && !props.hideProductVariationMenu && (
            <ProductVariationIcon onClick={onMenuClick} disabled={isViewOnly}>
              <MoreVertRounded />
            </ProductVariationIcon>
          )}
        </ProductVariationHeader>
      </Grid>
      <StyledGrid container spacing={2}>
        <Grid item xs={12} md={6}>
          <Controller
            name={`productVariations.${props.index}.product_variation_size_id`}
            rules={{ required: lang.errors.required }}
            control={control}
            render={({ field }) => (
              <SelectDropdownWidget
                {...field}
                selectOptions={getProductVariationTypeOptions()}
                value={
                  !isEmpty(getProductVariationTypeOptions()) ? field.value : ''
                }
                label={lang.variationType}
                hasError={Boolean(errors?.product_variation_size_id?.message)}
                error={errors?.product_variation_size_id?.message}
                disabled={isViewOnly}
                options={{ showFooterButton: true }}
                footerButtonLabel={lang.addNewVariationType}
                onFooterButtonClick={createVariationSizeModel.onOpenModal}
              />
            )}
          />
        </Grid>

        <Grid item xs={12} md={6}>
          <Controller
            name={`productVariations.${props.index}.product_code`}
            rules={{
              ...(props.index !== 0 && { required: lang.errors.required }),
              pattern: {
                value: /^[A-Z0-9-_]{1,255}$/,
                message: lang.invalidProductCode,
              },
              validate: onValidate,
            }}
            control={control}
            render={({ field }) => (
              <InputWidget
                {...field}
                label={
                  props.index === 0
                    ? lang.productCode
                    : lang.productCodeRequired
                }
                hasError={Boolean(errors?.product_code?.message)}
                error={errors?.product_code?.message}
                disabled={!props.isProductCodeEnabled || isViewOnly}
              />
            )}
          />
        </Grid>

        <Grid item xs={12} md={6}>
          <Controller
            name={`productVariations.${props.index}.name`}
            rules={{
              required: lang.errors.required,
              pattern: {
                value: /^(?!\s*$).+/,
                message: `${lang.noProductName}`,
              },
            }}
            control={control}
            render={({ field }) => (
              <InputWidget
                {...field}
                label={lang.productNameRequired}
                hasError={Boolean(errors?.name?.message)}
                error={errors?.name?.message}
                disabled={isViewOnly}
              />
            )}
          />
        </Grid>

        <Grid item xs={12} md={6}>
          <Controller
            name={`productVariations.${props.index}.active`}
            control={control}
            render={({ field }) => (
              <SelectDropdownWidget
                {...field}
                label={lang.statusRequired}
                selectOptions={statusOptions}
                disabled={isViewOnly}
              />
            )}
          />
        </Grid>

        <Grid item xs={12}>
          <Controller
            name={`productVariations.${props.index}.description`}
            control={control}
            render={({ field }) => (
              <InputWidget
                {...field}
                label={lang.description}
                multiline
                rows={4}
                disabled={isViewOnly}
              />
            )}
          />
        </Grid>

        <Grid item xs={12}>
          <Controller
            name={`productVariations.${props.index}.disclaimer`}
            control={control}
            render={({ field }) => (
              <InputWidget
                {...field}
                label={lang.disclaimer}
                disabled={isViewOnly}
              />
            )}
          />
        </Grid>

        <Grid item xs={12} md={4}>
          <Controller
            name={`productVariations.${props.index}.nutrition`}
            control={control}
            render={({ field }) => (
              <InputWidget
                {...field}
                label={lang.nutrition}
                disabled={isViewOnly}
              />
            )}
          />
        </Grid>

        <Grid item xs={12} md={4}>
          <Controller
            name={`productVariations.${props.index}.volume`}
            control={control}
            render={({ field }) => (
              <NumberInputWidget
                {...field}
                label={lang.volume}
                options={{ min: 0 }}
                disabled={isViewOnly}
              />
            )}
          />
        </Grid>

        <Grid item xs={12} md={4}>
          <Controller
            name={`productVariations.${props.index}.product_variation_flavor_id`}
            rules={{ required: lang.errors.required }}
            control={control}
            render={({ field }) => (
              <SelectDropdownWidget
                selectOptions={productVariationFlavorOptions}
                {...field}
                label={lang.flavorRequired}
                hasError={Boolean(errors?.product_variation_flavor_id?.message)}
                error={errors?.product_variation_flavor_id?.message}
                disabled={isViewOnly}
                onFooterButtonClick={createVariationFlavorModel.onOpenModal}
                footerButtonLabel={lang.addNewFlavor}
                options={{ showFooterButton: true }}
              />
            )}
          />
        </Grid>
        <Grid item xs={12}>
          <ProductVariationDyDisplay
            productVariation={props.productVariation}
            productVariationTypes={productVariationTypes}
            productVariationFlavors={productVariationFlavors}
            onDeleteDyDisplay={handleDeleteDyDisplay}
            isAddDyDisplayDisabled={deleteDyDisplay.isLoading}
            onBeforeNavigate={props.onBeforeDyNavigation}
          />
        </Grid>
        <Grid item xs={12}>
          <ProductVariationAssets
            themes={props.themes}
            variationsAssets={variationAssets}
            onChangeVariationTheme={onChangeVariationTheme}
            onChangeVariationAsset={onChangeVariationAsset}
            onChangeVariationDY={onChangeVariationDY}
            deleteVariationAsset={deleteVariationAsset}
            groupProductAsset={props.groupProductImage}
          />
        </Grid>
      </StyledGrid>
      <ProductVariationMenu
        anchorEl={productVariationMenu.anchorEl}
        onClose={productVariationMenu.onCloseMenu}
        productVariationsCount={productVariations.length}
        index={props.index}
        onDeleteClick={deleteProductVariationModal.onOpenModal}
      />
      <SimpleDialog
        actionColor="error"
        modalSize="sm"
        open={deleteProductVariationModal.open}
        onClose={deleteProductVariationModal.onCloseModal}
        onClickCancel={deleteProductVariationModal.onCloseModal}
        disabled={deletingStatus}
        onClickConfirm={handleDeleteProductVariation}
        title={lang.deleteProductVariationModal.title}
        subTitle={lang.deleteProductVariationModal.subTitle}
        cancelText={lang.cancel}
        confirmText={lang.deleteText}
      />

      {createVariationSizeModel.open && (
        <CreateVariationSizeModel
          open={createVariationSizeModel.open}
          onClose={createVariationSizeModel.onCloseModal}
        />
      )}
      {createVariationFlavorModel.open && (
        <CreateVariationFlavorModal
          open={createVariationFlavorModel.open}
          onClose={createVariationFlavorModel.onCloseModal}
        />
      )}
      {blockingDeletionModal.open && (
        <SimpleDialog
          open={blockingDeletionModal.open}
          onClose={blockingDeletionModal.onCloseModal}
          onClickCancel={blockingDeletionModal.onCloseModal}
          title={lang.blockingDeletionModal.title}
          subTitle={lang.blockingDeletionModal.subTitle}
          cancelText={lang.close}
          modalSize="sm"
          hideConfirm
        />
      )}
    </Grid>
  );
};
